import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

/**
 * Reusable click-outside popover.
 *
 * Renders `content` in a document.body portal so overflow-hidden ancestors
 * (sidebar, cards) can't clip it. Popover follows the trigger on
 * scroll/resize and stays inside the viewport.
 */
export function Popover({
  trigger,
  content,
  width = 420,
  align = 'right',
  className,
}: {
  trigger: (props: {
    onClick: () => void;
    ref: React.RefObject<HTMLDivElement | null>;
  }) => ReactNode;
  content: (close: () => void) => ReactNode;
  /** Preferred popover width in px. Auto-shrinks near viewport edges. */
  width?: number;
  align?: 'left' | 'right';
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width });

  const compute = () => {
    const t = triggerRef.current?.getBoundingClientRect();
    if (!t) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = Math.min(width, vw - 24);
    let left = align === 'right' ? t.right - w : t.left;
    if (left < 12) left = 12;
    if (left + w > vw - 12) left = vw - 12 - w;
    let top = t.bottom + 4;
    // Flip up if it would go below viewport bottom.
    if (top + 320 > vh - 12) {
      const above = t.top - 4 - 320;
      if (above > 12) top = t.top - 4;
    }
    setPos({ top, left, width: w });
  };

  useLayoutEffect(() => {
    if (open) compute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = () => compute();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (
        !triggerRef.current?.contains(e.target as Node) &&
        !popoverRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <>
      {trigger({ onClick: () => setOpen((v) => !v), ref: triggerRef })}
      {open &&
        createPortal(
          <div
            ref={popoverRef}
            style={{
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              width: pos.width,
            }}
            className={cn(
              'glass-float z-[100] rounded-md border bg-card shadow-2xl',
              className
            )}
          >
            {content(() => setOpen(false))}
          </div>,
          document.body
        )}
    </>
  );
}
